//
// To all developers:
//
// In all cases where a significant event happened (data loaded, error, you name it)
// please raise a custom event and document it here, like the examples below.
// All global events are to be dispatched against the document.body element - this is
// the so called application event bus for application-wide events.
//
// !!! Please don't use jQuery for dispatching/handling events !!!
//
// Payload is _always_ an object with fields. Fields need to be defined according to the spec
// described in "createEvent" documentation (see at the bottom of this file)
//

import log from 'loglevel'

// Raised when search data is received
export const searchDataReceived = createEvent('grecos:search:server-data-received', {
  minDate: { required: true, type: 'Moment', description: 'Earilest available date' },
  maxDate: { required: true, type: 'Moment', description: 'Latest available date' },
  validRanges: { required: true, type: 'Array', description: 'Array of valid ranges' },
})

// Raised when offers are loaded
export const offersLoaded = createEvent('grecos:search:offers-loaded', {
  offers: { required: true, type: 'Array', description: 'Array of loaded offers' },
})

// Raised when the list of forms on the page changes
export const formsListUpdated = createEvent('grecos:forms-list-updated')

// 
export const appBeforeInitialize = createEvent('grecos:app:before-initialize')

// Raised when the application is fully initialized
export const appInitialized = createEvent('grecos:app:initialized')

// Raised to hide all calendars
export const closeCalendars = createEvent('grecos:calendar:close')

// Raised when list of offers changes
export const offersListUpdated = createEvent('grecos:offers:list-updated', {
  offers: { required: true, type: 'Array', description: 'New list of offers' },
  offersCount: { required: true, type: 'Integer', description: 'Offers count' },
  component: { required: true, type: 'Object', description: 'Component that you may reference if you need to redo the request' },
})

export const hotelNavigationUpdated = createEvent('grecos:hotel-navigation-updated')

export const bookingOrderCreate = createEvent('grecos:order-create')

// Raised when filters on offers list change
export const offersListFilterChanged = createEvent('grecos:offers:filter-changed', {
  orderBy: { required: true, type: 'Object', description: 'Field to sort by (price/name/standard)' },
  direction: { required: true, type: 'String', description: 'Sort direction (asc/desc)' },
  priceType: { required: true, type: 'String', description: 'Type of price (person, group)' },
  viewType: { required: true, type: 'String', description: 'Type of view to show (row, tile, map)' },
})

// ----------------------------------------------------------------------------

/**
 * The element to which all application-wide event handlers will be attached
 */
const masterEventsElement = document.body

/**
 * Creator for events
 * 
 * Each event consists of the main handler that executes upon the application of
 * call operator () and two maintenance methods: "subscribe" and "unsubscribe".
 * 
 * The definition of an event handler consists of 2 things: the name of the actual
 * event and the list of fields expected in the payload.
 * Each field definition can have up to 3 parameters:
 *   "required" which defines if the field is required
 *   "type" which is a string that will be compared against "field.constructor.name"
 *   "description" whilch will be added to error messages
 * 
 * See searchDataReceived for a complete example
 */
function createEvent (name, api = {}) {
  if (!name.startsWith('grecos:'))
    throw new Error('Each event name must start with "grecos:"!')

  const fieldExists = (field, payload) => api[field].required && payload[field] !== undefined
  const isValidValue = (field, payload) => payload[field] !== null || api[field].nullable
  const fieldIsDefined = field => api[field] !== undefined && api[field] !== null && api[field].type

  const checkPayloadFields = payload => {
    Object.keys(api).forEach(field => {
      if (!fieldExists(field, payload))
        throw Error(`Missing field ${field} while triggering event ${name} (${api[field].description})`)
      if (!isValidValue(field, payload))
        throw new Error(`Field ${field} is null but not marked as nullable (${api[field].description})`)
    })
    Object.keys(payload).forEach(field => {
      if (!fieldIsDefined(field))
        throw new Error(`Unknown payload field ${field} while triggering event ${name}. If that is a new field please update the definition in events.js accordingly!`)
    })
  }

  const event = (payload = {}) => {
    checkPayloadFields(payload)
    log.debug('Triggering event', name)
    masterEventsElement.dispatchEvent(new CustomEvent(name, { detail: payload }))
  }

  event.subscribe = fn => {
    log.debug('Subscribing to event', name)
    masterEventsElement.addEventListener(name, fn)
  }

  event.unsubscribe = fn => {
    log.debug('Unsubscribing from event', name)
    masterEventsElement.removeEventListener(event, fn)
  }

  return event
}

document.addEventListener('readystatechange', e => {
  if (e.target.readyState === 'complete') {
    appBeforeInitialize()
  }
})


// WEBPACK FOOTER //
// ./src/events.js